import React, { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/compat/router';
import { FieldLabel } from '@puckeditor/core';
import type { CustomField } from '@puckeditor/core';

import { useCmsEnterpriseId } from '../cms-enterprise-context';
import type { DestinationOption } from './destination-picker-modal';

// Lazy so `@apollo/client` and the generated `CmsPagesForDestinationPicker` document stay out of
// the storefront bundle. `fields` objects are reachable from the production renderer (cms/AGENTS.md
// "Known caveat, not yet solved"), so a static import here would ship Apollo client weight to shoppers.
// Enforced by `bundle-boundaries.test.ts`.
const DestinationPickerModal = lazy(() => import('./destination-picker-modal'));

export type DestinationFieldProps = {
  field: { label?: string };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

export type DestinationMode = 'external' | 'internal';

/**
 * Infers initial mode from an absolute URI scheme or protocol-relative URL.
 * Root-relative storefront paths and fragments stay internal. The field never mutates or rewrites
 * the stored value on its own, including unsupported legacy schemes that consumers may later reject.
 */
export function inferMode(value: string): DestinationMode {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(value) ? 'external' : 'internal';
}

/**
 * CSS custom properties with literal fallbacks, nesting `--puck-*` where the value should
 * follow Puck's chrome palette (cms/AGENTS.md gotcha #11).
 */
const colors = {
  border: 'var(--dcm-cms-destination-field-border-color, var(--puck-color-border, #d0d4d8))',
  tabBg: 'var(--dcm-cms-destination-field-tab-bg, var(--puck-color-surface, #fff))',
  tabActiveBg: 'var(--dcm-cms-destination-field-tab-active-bg, var(--puck-color-interactive-soft, #e1f1eb))',
  accent: 'var(--dcm-cms-destination-field-accent-color, var(--puck-color-interactive, #004c3f))',
  accentContrast: 'var(--dcm-cms-destination-field-accent-contrast-color, var(--puck-color-white, #fff))',
  surfaceMuted: 'var(--dcm-cms-destination-field-surface-muted-bg, var(--puck-color-surface-muted, #f8faf9))',
  textMuted: 'var(--dcm-cms-destination-field-muted-text-color, var(--puck-color-text-secondary, #646d72))',
};

const radii = {
  sm: 'var(--dcm-cms-destination-field-radius-sm, 4px)',
  md: 'var(--dcm-cms-destination-field-radius-md, 8px)',
  button: 'var(--dcm-cms-destination-field-button-radius, 7px)',
};

const columnStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 8 };

const tabRowStyle: React.CSSProperties = {
  display: 'grid',
  gridAutoFlow: 'column',
  gridAutoColumns: '1fr',
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  overflow: 'hidden',
};

const tabButtonBaseStyle: React.CSSProperties = {
  border: 0,
  background: colors.tabBg,
  padding: '8px 6px',
  fontSize: 12,
  cursor: 'pointer',
};

const tabButtonActiveStyle: React.CSSProperties = {
  background: colors.tabActiveBg,
  color: colors.accent,
  fontWeight: 750,
};

const selectedPageRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  border: `1px solid ${colors.border}`,
  borderRadius: radii.md,
  padding: '8px 10px',
  background: colors.surfaceMuted,
};

const chooseButtonStyle: React.CSSProperties = {
  border: 0,
  background: colors.accent,
  color: colors.accentContrast,
  borderRadius: radii.button,
  padding: '8px 12px',
  fontWeight: 750,
  fontSize: 13,
  cursor: 'pointer',
  flex: 'none',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '6px 8px',
  border: `1px solid ${colors.border}`,
  borderRadius: radii.sm,
  fontSize: 14,
  boxSizing: 'border-box',
};

const hintStyle: React.CSSProperties = { marginTop: 0, fontSize: 12, color: colors.textMuted };

const selectedTextStyle: React.CSSProperties = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  fontSize: 13,
};

/**
 * Puck custom field for link destinations.
 *
 * Persists a single flat string. Existing/unknown string values survive render and editor mount
 * without rewriting.
 *
 * Active preview store authoritativeness contract:
 * The active router.query.cName preview store is intentionally authoritative. Website cName,
 * default dispensary, or sibling stores must NOT be substituted when router.query.cName is missing
 * or invalid. In multi-dispensary enterprises, substituting a website or sibling store would cause
 * cross-store route pollution or invalid links. When an active store cName is unavailable, internal
 * page picking is disabled/hidden, while manual text input and external URL mode remain fully functional.
 */
export function DestinationField({ field, name, value, onChange, readOnly }: DestinationFieldProps): JSX.Element {
  const router = useRouter();
  const rawCName = typeof router?.query.cName === 'string' ? router.query.cName.trim() : '';
  const exactStoreCName = rawCName.length > 0 ? rawCName : undefined;
  const enterpriseId = useCmsEnterpriseId();

  const resolvedValue = typeof value === 'string' ? value : '';
  const [mode, setMode] = useState<DestinationMode>(() => inferMode(resolvedValue));
  const [isOpen, setIsOpen] = useState(false);
  const [lastPicked, setLastPicked] = useState<DestinationOption | null>(null);
  const chooseButtonRef = useRef<HTMLButtonElement>(null);

  const canChoosePage = Boolean(enterpriseId && exactStoreCName && !readOnly);

  const handleTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.currentTarget.value);
    },
    [onChange]
  );

  const handleOpen = useCallback(() => {
    if (canChoosePage) setIsOpen(true);
  }, [canChoosePage]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    chooseButtonRef.current?.focus();
  }, []);

  const handleSelect = useCallback(
    (option: DestinationOption) => {
      onChange(option.path);
      setLastPicked(option);
      setIsOpen(false);
      chooseButtonRef.current?.focus();
    },
    [onChange]
  );

  function resolveSummaryText(): string {
    if (resolvedValue === '') return 'No page selected yet';
    if (lastPicked?.path === resolvedValue) return `${lastPicked.label} · ${resolvedValue}`;
    return resolvedValue;
  }
  const summaryText = resolveSummaryText();

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly} el='div'>
      <div style={columnStyle}>
        <div style={tabRowStyle} role='tablist' aria-label='Destination type'>
          <button
            type='button'
            role='tab'
            aria-selected={mode === 'internal'}
            data-testid={`destination-tab-internal-${name}`}
            style={mode === 'internal' ? { ...tabButtonBaseStyle, ...tabButtonActiveStyle } : tabButtonBaseStyle}
            onClick={() => setMode('internal')}
            disabled={readOnly}
          >
            Internal page
          </button>
          <button
            type='button'
            role='tab'
            aria-selected={mode === 'external'}
            data-testid={`destination-tab-external-${name}`}
            style={mode === 'external' ? { ...tabButtonBaseStyle, ...tabButtonActiveStyle } : tabButtonBaseStyle}
            onClick={() => setMode('external')}
            disabled={readOnly}
          >
            External URL
          </button>
        </div>

        {mode === 'internal' ? (
          <>
            <div style={selectedPageRowStyle}>
              <div>
                <div style={hintStyle}>Selected page</div>
                <div style={selectedTextStyle} data-testid={`destination-selected-${name}`}>
                  {summaryText}
                </div>
              </div>
              {enterpriseId && exactStoreCName && (
                <button
                  ref={chooseButtonRef}
                  type='button'
                  onClick={handleOpen}
                  disabled={readOnly}
                  data-testid={`destination-choose-${name}`}
                  style={chooseButtonStyle}
                >
                  Choose page
                </button>
              )}
            </div>
            <input
              type='text'
              value={resolvedValue}
              onChange={handleTextChange}
              disabled={readOnly}
              placeholder='/stores/...'
              data-testid={`destination-input-${name}`}
              style={inputStyle}
            />
            <p style={hintStyle}>Or type a relative path directly, e.g. /about-us.</p>
          </>
        ) : (
          <>
            <input
              type='text'
              value={resolvedValue}
              onChange={handleTextChange}
              disabled={readOnly}
              placeholder='https://example.com'
              data-testid={`destination-input-${name}`}
              style={inputStyle}
            />
            <p style={hintStyle}>Full URL for destinations outside this storefront.</p>
          </>
        )}
      </div>

      {isOpen && enterpriseId && exactStoreCName && (
        <Suspense fallback={null}>
          <DestinationPickerModal
            key={exactStoreCName}
            enterpriseId={enterpriseId}
            cName={exactStoreCName}
            onSelect={handleSelect}
            onClose={handleClose}
          />
        </Suspense>
      )}
    </FieldLabel>
  );
}

export type DestinationFieldOptions = {
  /** LLM-facing instructions, matching `urlField`/`selectField` convention. */
  instructions?: string;
};

/**
 * Builds a Puck `custom` destination field with AI schema metadata.
 */
export function destinationField(label: string, options: DestinationFieldOptions = {}): CustomField<string> {
  const { instructions } = options;
  return {
    type: 'custom',
    label,
    render: DestinationField as CustomField<string>['render'],
    ai: {
      schema: { type: 'string' },
      stream: false,
      ...(instructions ? { instructions } : {}),
    },
  };
}
